import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useRef } from 'react';
import { Box, Button, Stack } from '@mui/material';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import TipTapEditor, { TipTapEditorHandle } from '../common/TipTapEditor';
import DiskussionLinkPicker from './DiskussionLinkPicker';
import { diskussionService } from '../../services/diskussionService';
import { API_BASE } from '../../services/api';
import ChecklistMarker from './extensions/ChecklistMarker';

export interface DiskussionRichEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  label?: string;
  minHeight?: number;
  placeholder?: string;
  /**
   * Wenn true, kann eine Checkliste eingefuegt werden (typischerweise nur fuer den ersten Post
   * oder beim Bearbeiten eines Posts, der bereits eine enthaelt).
   */
  allowChecklist?: boolean;
}

const CHECKLIST_MARKER_ANY_RE = /\[\[aio:checklist:[\w-]+\]\]/;

/** TipTap-basiertes WYSIWYG-Eingabefeld fuer Diskussionen mit Paste-Upload und Link-Picker. */
const DiskussionRichEditor = forwardRef<TipTapEditorHandle, DiskussionRichEditorProps>(({
  value,
  onChange,
  disabled = false,
  label,
  minHeight = 160,
  placeholder,
  allowChecklist = false,
}, ref) => {
  const editorRef = useRef<TipTapEditorHandle | null>(null);

  useImperativeHandle(ref, () => ({
    insertImage: (url, alt) => editorRef.current?.insertImage(url, alt),
    insertCodeBlock: (content) => editorRef.current?.insertCodeBlock(content),
    insertText: (text) => editorRef.current?.insertText(text),
  }));

  const handlePasteImage = useCallback(async (file: File): Promise<string | null> => {
    try {
      const { file_id } = await diskussionService.uploadBild(file);
      return `${API_BASE}/files/${file_id}/preview`;
    } catch (err) {
      console.error('Diskussion Bild-Upload fehlgeschlagen:', err);
      return null;
    }
  }, []);

  const handleInsertSnippet = useCallback((snippet: string) => {
    if (editorRef.current) {
      editorRef.current.insertText(snippet);
    } else {
      onChange((value || '') + snippet);
    }
  }, [onChange, value]);

  const insertChecklist = useCallback(() => {
    handleInsertSnippet('<p>[[aio:checklist:NEW]]</p>');
  }, [handleInsertSnippet]);

  const hasChecklistMarker = CHECKLIST_MARKER_ANY_RE.test(value || '');
  const extraExtensions = useMemo(
    () => (allowChecklist ? [ChecklistMarker] : []),
    [allowChecklist],
  );

  return (
    <Box>
      <TipTapEditor
        ref={editorRef}
        content={value}
        onChange={onChange}
        editable={!disabled}
        placeholder={placeholder}
        minHeight={minHeight}
        label={label}
        onPasteImage={handlePasteImage}
        extraExtensions={extraExtensions}
      />
      {allowChecklist && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<CheckBoxIcon />}
            onClick={insertChecklist}
            disabled={disabled || hasChecklistMarker}
          >
            Checkliste einfügen
          </Button>
        </Stack>
      )}
      <DiskussionLinkPicker disabled={disabled} onInsert={handleInsertSnippet} />
    </Box>
  );
});

DiskussionRichEditor.displayName = 'DiskussionRichEditor';

export default DiskussionRichEditor;
